<!-- src/components/MySettings.vue -->
<template>
  <!-- Profile Edit Dialog -->
  <Transition name="dialog">
    <div class="dialog-overlay" v-if="isDialogOpen" @click.self="isDialogOpen = null">
      <div class="dialog-content" v-if="isDialogOpen === 'profile'">
        <div class="dialog-header">
          <h2>내 정보 수정</h2>
          <button class="close-btn" @click="isDialogOpen = null">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>
        
        <form class="profile-form" @submit.prevent="saveUserInfo">
          <div class="avatar-upload">
            <label for="profileImgInput">
              <div class="avatar-wrapper">
                <img :src="userInfo.image" alt="Profile" />
                <div class="avatar-overlay">
                  <i class="ph-bold ph-camera"></i>
                  <span>변경</span>
                </div>
              </div>
            </label>
            <input type="file" id="profileImgInput" @change="profileImgOnFileChange" hidden accept="image/*" />
          </div>
          
          <div class="form-fields">
            <div class="field">
              <label>이름</label>
              <input type="text" v-model="userInfo.name" readonly class="readonly" />
            </div>
            <div class="field">
              <label>닉네임</label>
              <input type="text" v-model="userInfo.nickname" placeholder="닉네임을 입력하세요" />
            </div>
            <div class="field">
              <label>소개</label>
              <textarea v-model="userInfo.introduce" placeholder="나를 소개해주세요" rows="3"></textarea>
            </div>
          </div>
          
          <div class="dialog-actions">
            <button type="button" class="btn-secondary" @click="isDialogOpen = null">취소</button>
            <button type="submit" class="btn-primary">저장하기</button>
          </div>
        </form>
      </div>
    </div>
  </Transition>

  <div class="settings-page">
    <!-- Header -->
    <header class="page-header">
      <button class="back-btn" onclick="location.replace('/')">
        <i class="ph-bold ph-arrow-left"></i>
      </button>
      <div class="header-title">
        <h1>설정</h1>
        <span class="subtitle">계정 및 앱 환경설정</span>
      </div>
    </header>

    <!-- Loading State -->
    <div class="loading-state" v-if="!userSettings[0]">
      <div class="loader"></div>
      <span>설정을 불러오는 중...</span>
    </div>

    <!-- Main Content -->
    <main class="settings-main" v-if="userSettings[0]">
      
      <!-- Profile Section -->
      <section class="settings-section profile-section" @click="isDialogOpen = 'profile'">
        <div class="section-icon">
          <i class="ph-bold ph-user-circle"></i>
        </div>
        <div class="profile-card">
          <div class="profile-info">
            <div class="avatar">
              <img :src="userInfo.image" alt="Profile" />
              <span class="badge">
                <img src="https://amadiary.com/pub/acon/ama_logo_bg.png" alt="AMA" />
              </span>
            </div>
            <div class="user-details">
              <h3>{{ userInfo.nickname || userInfo.name }}</h3>
              <p>{{ userInfo.email }}</p>
              <span class="account-type">AMA 계정</span>
            </div>
          </div>
          <div class="edit-indicator">
            <i class="ph-bold ph-pencil-simple"></i>
          </div>
        </div>
      </section>

      <!-- Organization Section -->
      <section class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-buildings"></i>
          </div>
          <div class="section-title">
            <h2>소속 그룹</h2>
            <span>조직 및 팀 정보</span>
          </div>
        </div>
        <div class="section-content">
          <div class="empty-state" v-if="groupMember.length === 0">
            <i class="ph-bold ph-users-three"></i>
            <p>소속된 그룹이 없습니다</p>
            <button class="btn-ghost">그룹 찾아보기</button>
          </div>
        </div>
      </section>

      <!-- Security Section -->
      <section class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-shield-check"></i>
          </div>
          <div class="section-title">
            <h2>보안</h2>
            <span>계정 보안 설정</span>
          </div>
        </div>
        <div class="section-content">
          <div class="setting-item">
            <div class="setting-info">
              <i class="ph-bold ph-key"></i>
              <div>
                <h4>비밀번호 변경</h4>
                <p>아마다이어리에서 비밀번호를 변경할 수 있습니다</p>
              </div>
            </div>
            <a href="https://amadiary.com" class="btn-link" target="_blank">
              이동 <i class="ph-bold ph-arrow-square-out"></i>
            </a>
          </div>
          <div class="setting-item">
            <div class="setting-info">
              <i class="ph-bold ph-fingerprint"></i>
              <div>
                <h4>2단계 인증</h4>
                <p>추가 보안을 위한 인증 설정</p>
              </div>
            </div>
            <span class="coming-soon">Coming Soon</span>
          </div>
        </div>
      </section>

      <!-- Subscription Section -->
      <section class="settings-section" @click="$router.push('/subscription')">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-credit-card"></i>
          </div>
          <div class="section-title">
            <h2>구독 플랜</h2>
            <span>결제 및 구독 관리</span>
          </div>
        </div>
        <div class="section-content">
          <div class="setting-item">
            <div class="setting-info">
              <i class="ph-bold ph-crown"></i>
              <div>
                <h4>플랜 관리</h4>
                <p>구독 플랜 변경, 결제 내역 확인</p>
              </div>
            </div>
            <i class="ph-bold ph-caret-right"></i>
          </div>
        </div>
      </section>

      <!-- Token Charge Section -->
      <section class="settings-section" @click="$router.push('/token-charge')">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-coin"></i>
          </div>
          <div class="section-title">
            <h2>토큰 충전</h2>
            <span>토큰 구매 및 충전 내역</span>
          </div>
        </div>
        <div class="section-content">
          <div class="setting-item">
            <div class="setting-info">
              <i class="ph-bold ph-lightning"></i>
              <div>
                <h4>토큰 충전하기</h4>
                <p>SMS, AI 분석 등에 사용되는 토큰 충전</p>
              </div>
            </div>
            <i class="ph-bold ph-caret-right"></i>
          </div>
        </div>
      </section>

      <!-- Notification Section -->
      <section class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-bell"></i>
          </div>
          <div class="section-title">
            <h2>알림</h2>
            <span>알림 수신 설정</span>
          </div>
        </div>
        <div class="section-content">
          <div class="toggle-group">
            <div class="toggle-item">
              <div class="toggle-info">
                <i class="ph-bold ph-device-mobile"></i>
                <span>푸시 알림</span>
              </div>
              <toggleBtnVue 
                :target="userSettings[0].allow_push"
                @update="(val) => { 
                  userSettings[0].allow_push = val;
                  updateUserSettings();
                }"
              />
            </div>
            <div class="toggle-item">
              <div class="toggle-info">
                <i class="ph-bold ph-envelope-simple"></i>
                <span>이메일 알림</span>
              </div>
              <toggleBtnVue 
                :target="userSettings[0].allow_email"
                @update="(val) => { 
                  userSettings[0].allow_email = val;
                  updateUserSettings();
                }"
              />
            </div>
            <div class="toggle-item">
              <div class="toggle-info">
                <i class="ph-bold ph-chat-text"></i>
                <span>SMS 알림</span>
              </div>
              <toggleBtnVue 
                :target="userSettings[0].allow_sms"
                @update="(val) => { 
                  userSettings[0].allow_sms = val;
                  updateUserSettings();
                }"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Appearance Section -->
      <section class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="ph-bold ph-paint-brush"></i>
          </div>
          <div class="section-title">
            <h2>외관</h2>
            <span>테마 및 배경 설정</span>
          </div>
        </div>
        <div class="section-content">
          <div class="appearance-grid">
            <div class="appearance-item">
              <h4>테마</h4>
              <ThemeManager />
            </div>
            <div class="appearance-item wallpaper">
              <h4>배경 이미지</h4>
              <div class="wallpaper-preview">
                <label for="bgImgInput">
                  <div class="preview-box" :class="{ 'has-image': userSettings[0]?.bg_img }">
                    <img v-if="userSettings[0]?.bg_img" :src="userSettings[0]?.bg_img" alt="Background" />
                    <div class="upload-placeholder" v-else>
                      <i class="ph-bold ph-image"></i>
                      <span>이미지 선택</span>
                    </div>
                    <div class="preview-overlay">
                      <i class="ph-bold ph-upload-simple"></i>
                    </div>
                  </div>
                </label>
                <input type="file" id="bgImgInput" hidden @change="bgImgOnFileChange" accept="image/*" />
              </div>
              <button class="btn-text" @click="restBG" v-if="userSettings[0]?.bg_img">
                <i class="ph-bold ph-arrow-counter-clockwise"></i>
                기본으로 되돌리기
              </button>
            </div>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script>
import { inject, ref, watch } from 'vue';
import toggleBtnVue from '../ui/toggleBtn.vue';
import ThemeManager from '@/components/dialogs/themeManager.vue';
 
export default {
  name: "MySettings",
  components: {
    toggleBtnVue,
    ThemeManager
  },
  setup() {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');
    const getUserProfileInfo = inject('getUserProfileInfo');
    const userInfo = ref({});
    const groupMember = ref({});
    const userSettings = ref({});
    const isDialogOpen = ref(null);

    async function profileImgOnFileChange(e) {
      const input = e.target;
      const file = input.files ? input.files[0] : null;
      if (!file) return;

      userInfo.value.image = URL.createObjectURL(file);

      const formData = new FormData();
      formData.append('idx', getMbid() || '');
      formData.append('table', 'member');
      formData.append('usage', 'profile_img');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData,
          mode: 'cors',
          credentials: 'include'
        });

        const data = await response.json();

        if (data.url) {
          userInfo.value.image = data.url;
          await sql_query(` update member set image = '${data.url}' where mb_id = '${getMbid()}' `);
        } else {
          alert("이미지 업로드 실패");
        }
      } catch (err) {
        console.error('업로드 중 오류:', err);
        alert("업로드 중 오류 발생");
      }
    }

    async function bgImgOnFileChange(e) {
      const input = e.target;
      const file = input.files ? input.files[0] : null;
      if (!file) return;

      if (userSettings.value[0]) {
        userSettings.value[0].bg_img = URL.createObjectURL(file);
      }

      const formData = new FormData();
      formData.append('idx', userInfo.value.idx || '');
      formData.append('table', 'user_info');
      formData.append('usage', 'bg');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData,
          mode: 'cors',
          credentials: 'include'
        });

        const data = await response.json();

        if (data.url) {
          if (userSettings.value[0]) {
            userSettings.value[0].bg_img = data.url;
          }
          await sql_query(` update user_settings set bg_img = '${data.url}' where mb_id = '${getMbid()}' `);
        } else {
          alert("이미지 업로드 실패");
        }
      } catch (err) {
        console.error('업로드 중 오류:', err);
        alert("업로드 중 오류 발생");
      }
    }

    async function fetchUserProfile() {
      let res = await getUserProfileInfo();
      userInfo.value = res;
    }
    fetchUserProfile();

    async function fetchGroupMember() {
      let res = await sql_query(`SELECT * FROM group_member WHERE mb_id = '${userInfo.value.mb_id}'`);
      groupMember.value = res;
    }
    fetchGroupMember();

    async function loadUserSetting() {
      await ensureAndList();
      let res = await sql_query(`SELECT * FROM user_settings WHERE mb_id = '${userInfo.value.mb_id}'`);
      userSettings.value = res;
    }
    loadUserSetting();

    async function ensureAndList() {
      await sql_query(`
        INSERT INTO user_settings (mb_id)
        SELECT m.mb_id
        FROM member m
        LEFT JOIN user_settings s ON s.mb_id = m.mb_id
        WHERE s.mb_id IS NULL
      `);

      const rows = await sql_query(`
        SELECT m.mb_id, m.name, m.nickname, m.email,
              s.theme, s.visibility, s.allow_push, s.allow_email, s.allow_sms,
              s.bg_img, s.created_at, s.updated_at
        FROM member m
        JOIN user_settings s ON s.mb_id = m.mb_id
        ORDER BY m.idx DESC
      `);

      return rows;
    }

    watch(userInfo, (newVal) => {
      if (newVal.mb_id) {
        loadUserSetting();
      }
    });

    function updateUserSettings() {
      sql_query(`
        UPDATE user_settings
        SET theme = '${userSettings.value[0].theme}',
            visibility = '${userSettings.value[0].visibility}',
            allow_push = ${userSettings.value[0].allow_push},
            allow_email = ${userSettings.value[0].allow_email},
            allow_sms = ${userSettings.value[0].allow_sms},
            bg_img = '${userSettings.value[0].bg_img}'
        WHERE mb_id = '${userInfo.value.mb_id}'
      `);
    }

    async function saveUserInfo() {
      await sql_query(`
        UPDATE member
        SET name = '${userInfo.value.name}',
            nickname = '${userInfo.value.nickname}',
            email = '${userInfo.value.email}'
        WHERE mb_id = '${userInfo.value.mb_id}'
      `);
      fetchUserProfile();
      isDialogOpen.value = null;
    }

    async function restBG() {
      sql_query(` update user_settings set bg_img = '' where mb_id = '${getMbid()}' `);
      if (userSettings.value[0]) {
        userSettings.value[0].bg_img = '';
      }
    }

    return {
      userInfo,
      groupMember,
      userSettings,
      isDialogOpen,
      updateUserSettings,
      profileImgOnFileChange,
      saveUserInfo,
      restBG,
      bgImgOnFileChange
    }
  }
}
</script>

<style scoped>
/* Base Variables - global.css 변수 활용 */
.settings-page {
  --settings-bg: var(--color-background);
  --settings-surface: var(--color-background-light);
  --settings-surface-hover: var(--color-surface-reverse);
  --settings-border: var(--color-surface-reverse);
  --settings-text: var(--color-text-primary);
  --settings-text-secondary: var(--color-text-secondary);
  --settings-accent: var(--color-brand);
  --settings-accent-soft: rgba(255, 165, 0, 0.12);
  --settings-radius: var(--radius-lg);
  --settings-radius-sm: var(--radius-md);
  --settings-transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  font-family: var(--font-family-base);
  width: 100%;
  height: 100%;
  background: var(--settings-bg);
  color: var(--settings-text);
  overflow-y: auto;
  overflow-x: hidden;
}

.settings-page::-webkit-scrollbar {
  width: 6px;
}

.settings-page::-webkit-scrollbar-track {
  background: transparent;
}

.settings-page::-webkit-scrollbar-thumb {
  background: var(--settings-border);
  border-radius: 3px;
}

/* Header */
.page-header {
  position: sticky;
  top: 0;
  z-index: var(--z-index-base);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(to bottom, var(--settings-bg) 60%, transparent);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}

.back-btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 12px;
  color: var(--settings-text);
  font-size: var(--font-size-lg);
  cursor: pointer;
  transition: var(--settings-transition);
}

.back-btn:hover {
  background: var(--settings-surface-hover);
  transform: translateX(-2px);
}

.header-title h1 {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.02em;
}

.header-title .subtitle {
  font-size: var(--font-size-sm);
  color: var(--settings-text-secondary);
  margin-top: 2px;
  display: block;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
  color: var(--settings-text-secondary);
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid var(--settings-border);
  border-top-color: var(--settings-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Main Content */
.settings-main {
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Settings Section */
.settings-section {
  background: var(--color-surface-deep);
  border: 1px solid var(--settings-border);
  border-radius: var(--settings-radius);
  overflow: hidden;
  transition: var(--settings-transition);
  backdrop-filter: blur(var(--effect-blur));
}

.settings-section:hover {
  border-color: var(--settings-accent);
  box-shadow: 0 0 20px var(--settings-accent-soft);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  border-bottom: 1px solid var(--settings-border);
}

.section-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--settings-accent-soft);
  border-radius: 12px;
  color: var(--settings-accent);
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.section-title h2 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.section-title span {
  font-size: var(--font-size-sm);
  color: var(--settings-text-secondary);
}

.section-content {
  padding: 20px;
}

/* Profile Section */
.profile-section {
  cursor: pointer;
  padding: 20px;
  background: linear-gradient(135deg, var(--color-surface-deep) 0%, var(--settings-accent-soft) 100%);
}

.profile-section:hover {
  background: linear-gradient(135deg, var(--settings-surface-hover) 0%, var(--settings-accent-soft) 100%);
}

.profile-section .section-icon {
  position: absolute;
  opacity: 0;
}

.profile-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  position: relative;
  width: 64px;
  height: 64px;
}

.avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--settings-accent);
  box-shadow: 0 0 12px var(--settings-accent-soft);
}

.avatar .badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  background: var(--settings-surface);
  border: 2px solid var(--settings-bg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar .badge img {
  width: 14px;
  height: 14px;
  border: none;
  box-shadow: none;
}

.user-details h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.user-details p {
  margin: 4px 0 8px;
  font-size: var(--font-size-base);
  color: var(--settings-text-secondary);
}

.account-type {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: var(--settings-accent-soft);
  color: var(--settings-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: 20px;
  border: 1px solid var(--settings-accent);
}

.edit-indicator {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--settings-accent-soft);
  border-radius: 10px;
  color: var(--settings-accent);
  font-size: var(--font-size-lg);
  transition: var(--settings-transition);
}

.profile-section:hover .edit-indicator {
  background: var(--settings-accent);
  color: var(--color-text-black);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  color: var(--settings-text-secondary);
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  opacity: 0.3;
  margin-bottom: 12px;
}

.empty-state p {
  margin: 0 0 16px;
}

/* Setting Item */
.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid var(--settings-border);
}

.setting-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.setting-item:first-child {
  padding-top: 0;
}

.setting-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.setting-info > i {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--settings-bg);
  border-radius: 10px;
  font-size: var(--font-size-lg);
  color: var(--settings-text-secondary);
}

.setting-info h4 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.setting-info p {
  margin: 3px 0 0;
  font-size: var(--font-size-sm);
  color: var(--settings-text-secondary);
}

.coming-soon {
  padding: 6px 12px;
  background: var(--color-surface);
  border-radius: 20px;
  font-size: var(--font-size-xs);
  color: var(--settings-text-secondary);
  border: 1px solid var(--settings-border);
}

/* Toggle Group */
.toggle-group {
  display: flex;
  flex-direction: column;
}

.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid var(--settings-border);
}

.toggle-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.toggle-item:first-child {
  padding-top: 0;
}

.toggle-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-info i {
  font-size: var(--font-size-lg);
  color: var(--settings-text-secondary);
}

.toggle-info span {
  font-size: var(--font-size-base);
}

/* Appearance Grid */
.appearance-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.appearance-item h4 {
  margin: 0 0 14px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--settings-text-secondary);
}

.wallpaper-preview label {
  cursor: pointer;
  display: block;
}

.preview-box {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: var(--settings-bg);
  border: 2px dashed var(--settings-border);
  border-radius: var(--settings-radius-sm);
  overflow: hidden;
  transition: var(--settings-transition);
}

.preview-box.has-image {
  border-style: solid;
  border-color: var(--settings-accent);
}

.preview-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--settings-text-secondary);
}

.upload-placeholder i {
  font-size: 32px;
  opacity: 0.5;
}

.upload-placeholder span {
  font-size: var(--font-size-base);
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: var(--settings-transition);
}

.preview-overlay i {
  font-size: 28px;
  color: white;
}

.preview-box:hover .preview-overlay {
  opacity: 1;
}

.preview-box:hover {
  border-color: var(--settings-accent);
}

/* Buttons */
.btn-primary {
  padding: 12px 24px;
  background: var(--settings-accent);
  color: var(--color-text-black);
  border: none;
  border-radius: var(--settings-radius-sm);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: var(--settings-transition);
}

.btn-primary:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 16px var(--settings-accent-soft);
}

.btn-secondary {
  padding: 12px 24px;
  background: var(--settings-surface);
  color: var(--settings-text);
  border: 1px solid var(--settings-border);
  border-radius: var(--settings-radius-sm);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--settings-transition);
}

.btn-secondary:hover {
  background: var(--settings-surface-hover);
  border-color: var(--settings-accent);
}

.btn-ghost {
  padding: 10px 20px;
  background: transparent;
  color: var(--settings-accent);
  border: 1px solid var(--settings-accent);
  border-radius: var(--settings-radius-sm);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--settings-transition);
}

.btn-ghost:hover {
  background: var(--settings-accent-soft);
}

.btn-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--settings-accent);
  text-decoration: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  transition: var(--settings-transition);
}

.btn-link:hover {
  opacity: 0.8;
}

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 0;
  background: none;
  border: none;
  color: var(--settings-text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: var(--settings-transition);
}

.btn-text:hover {
  color: var(--settings-accent);
}

/* Dialog */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-top);
  padding: 20px;
}

.dialog-content {
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  background: var(--color-surface-mega);
  border: 1px solid var(--settings-border);
  border-radius: var(--settings-radius);
  overflow: hidden;
  backdrop-filter: blur(var(--blur));
  border-radius:16px;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--settings-border);
}

.dialog-header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--settings-bg);
  border: none;
  border-radius: 10px;
  color: var(--settings-text-secondary);
  font-size: var(--font-size-lg);
  cursor: pointer;
  transition: var(--settings-transition);
}

.close-btn:hover {
  color: var(--color-special-warning);
  background: rgba(218, 47, 71, 0.1);
}

/* Profile Form */
.profile-form {
  padding: 24px;
}

.avatar-upload {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.avatar-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  cursor: pointer;
}

.avatar-wrapper img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--settings-accent);
  transition: var(--settings-transition);
  box-shadow: 0 0 20px var(--settings-accent-soft);
}

.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: var(--settings-transition);
  color: white;
}

.avatar-overlay i {
  font-size: 24px;
}

.avatar-overlay span {
  font-size: var(--font-size-xs);
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.avatar-wrapper:hover img {
  border-color: var(--settings-accent);
  box-shadow: 0 0 30px var(--settings-accent-soft);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field label {
  display: block;
  margin-bottom: 8px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--settings-text-secondary);
}

.field input,
.field textarea {
  width: 100%;
  padding: 14px 16px;
  background: var(--settings-bg);
  border: 1px solid var(--settings-border);
  border-radius: var(--settings-radius-sm);
  color: var(--settings-text);
  font-size: var(--font-size-base);
  font-family: inherit;
  transition: var(--settings-transition);
  box-sizing: border-box;
}

.field input:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--settings-accent);
  box-shadow: 0 0 0 3px var(--settings-accent-soft);
}

.field input.readonly {
  opacity: 0.6;
  cursor: not-allowed;
}

.field textarea {
  resize: none;
  min-height: 80px;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  margin-top: 28px;
}

.dialog-actions button {
  flex: 1;
}

/* Dialog Transition */
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.25s ease;
}

.dialog-enter-active .dialog-content,
.dialog-leave-active .dialog-content {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

.dialog-enter-from .dialog-content,
.dialog-leave-to .dialog-content {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}

/* Mobile Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: 16px;
  }
  
  .header-title h1 {
    font-size: var(--font-size-lg);
  }
  
  .settings-main {
    padding: 0 16px 32px;
  }
  
  .section-header,
  .section-content,
  .profile-section {
    padding: 16px;
  }
  
  .profile-info {
    flex-direction: column;
    text-align: center;
  }
  
  .avatar {
    margin-bottom: 8px;
  }
  
  .dialog-content {
    max-height: 85vh;
  }
  
  .profile-form {
    padding: 20px;
  }
}
</style>